import isObject from "lodash/isObject"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import { PHASE_PRODUCTION_BUILD } from "next/constants"
import { fetchModel } from "@adobe/aem-react-editable-components"
import { SSRContext } from "contexts-lib/SSRContext"
import { transformModelWithStrings } from "utils-lib/ssr/transformModels"
import Layout from "@components/Layout/Layout"
import { LOCALES, AEM_STATIC_PAGES } from "@constants/pages"
import AEMContentParser from "@components/AEMContentParser"

const LOCAL_MODEL_PATH = "local"

export default function DynamicPage({
  modelPath = "",
  model = {},
  dataPageData = {},
}) {
  if (isEmpty(model)) return null

  /* 
    Sandbox POC pages:
    https://wastemanagement.atlassian.net/wiki/spaces/SEN/pages/2132738068/NextJS+Architecture+Sandbox
  */
  return (
    <SSRContext.Provider value={{ isSSR: true }}>
      <Layout model={model} dataPageData={dataPageData}>
        <section>
          <AEMContentParser model={model} modelPath={modelPath} />
        </section>
      </Layout>
    </SSRContext.Provider>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// DynamicPage.propTypes = {
//   model: PropTypes.object,
//   dataPageData: PropTypes.object,
//   modelPath: PropTypes.string,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// DynamicPage.defaultProps = {
//   model: {},
//   dataPageData: {},
//   modelPath: "",
// }

export async function getStaticPaths() {
  const paths = []

  // disable ssg in local parallel build, this prevents failures due to endpoint issues locally
  if (!process.env.NEXT_PUBLIC_SKIP_SSG) {
    AEM_STATIC_PAGES?.forEach((page) => {
      LOCALES.forEach((locale) => {
        if (page[locale]) {
          const [lang, country] = locale.toLowerCase().split("_")

          paths.push({
            params: {
              country,
              lang,
              modelPath: `${page.path}`.split("/"),
            },
          })
        }
      })
    })
  }

  return {
    paths,
    // this ensures that on an initial hit for a 404 page we won't return a 200 status with no content, instead we'll return a 404 status and the framework will serve our custom 404.js page
    fallback: "blocking",
  }
}

export async function getStaticProps({ params: { country, lang, modelPath } }) {
  // eslint-disable-next-line global-require
  const { Constants } = require("@adobe/aem-spa-page-model-manager")

  const cleanedPath = `/${country}/${lang}/${modelPath.join("/")}`

  let model = null
  if (
    process.env.NEXT_PUBLIC_USE_LOCAL_MODEL &&
    cleanedPath === `/${country}/${lang}/${LOCAL_MODEL_PATH}`
  ) {
    // enables dev from a local mock model
    // eslint-disable-next-line global-require
    const localModel = require("@data/localModel.json")
    model = {
      ...localModel,
      // transform the local model so it matches output of the fetchModel util
      cqType: localModel[Constants.TYPE_PROP],
      cqItems: localModel[Constants.ITEMS_PROP],
      cqItemsOrder: localModel[Constants.ITEMS_ORDER_PROP],
      cqPath: localModel[Constants.PATH_PROP],
      cqHierarchyType: localModel[Constants.HIERARCHY_TYPE_PROP],
    }
  } else {
    try {
      model = await fetchModel({
        pagePath: cleanedPath,
        host: process.env.NEXT_PUBLIC_AEM_HOST,
      })
    } catch (error) {
      console.error("Failed to fetch model:", error)
      // If the model fetch fails, return a 404 status and render the custom 404.js page
      return {
        notFound: true,
      }
    }

    // check that the model is valid json, if not return a 404
    // this handles bad requests that are not caught by the rewrites in next.config.js, or handled properly by AEM
    if (!model || !isObject(model) || isEmpty(model)) {
      console.error(
        "There is something wrong with the model, it is either missing, empty or not an object.",
        {
          isModelMissing: !model,
          isModelNotAnObject: !isObject(model),
          isModelEmpty: isEmpty(model),
        },
      )
      console.error("Here's the model we received from AEM:", model)

      return {
        notFound: true,
      }
    }

    const children = get(
      model,
      `cqItems.root[${Constants.ITEMS_PROP}].responsiveGrid[${Constants.ITEMS_PROP}]`,
      null,
    )

    const page404Path = `/${country}/${lang}/404`
    const currentPageIs404 = cleanedPath === page404Path
    const is404Model = model.cqPath === `/${country}/${lang}/errors/404`
    const isPlaceholderPage = isObject(children) && isEmpty(children)
    const isEmptyModel = isEmpty(model)

    // if the model is empty or has no components authored (such as one of the many placeholder page), redirect to 404 page.
    // this is an edge case but it could happen if someone knows the url and enters it in the browser
    // otherwise if the entered page doesn't exist and aem returns the error page model, redirect to the error page
    if (
      process.env.NEXT_PHASE !== PHASE_PRODUCTION_BUILD &&
      (isEmptyModel || isPlaceholderPage || (!currentPageIs404 && is404Model))
    ) {
      // If the route doesn't exist, return 404 status and render the custom 404.js page
      return { notFound: true }
    }
  }

  const transformedModel = transformModelWithStrings(model, true)

  const dataPage = await fetchModel({
    pagePath: `${country}/${lang}/${process.env.NEXT_PUBLIC_AEM_DATA_PAGE_PATH}`,
    host: process.env.NEXT_PUBLIC_AEM_HOST,
  })

  // extract what we need from the datapage
  const dataPageData =
    dataPage.cqItems.root[Constants.ITEMS_PROP].responsivegrid[
      Constants.ITEMS_PROP
    ]

  return {
    props: {
      modelPath: cleanedPath,
      model: {
        ...transformedModel,
        ssgTimestamp: new Date().toISOString(),
      },
      dataPageData,
    },
    revalidate: false,
  }
}
